import { useState, useCallback } from 'react';
import { useUIStore } from '../store';
import {
  uploadApi,
  validateMediaFile,
  getMediaCategory,
  FILE_ACCEPT_STRINGS,
} from '../api/upload.api';

/**
 * Custom hook for handling media file uploads with validation, progress tracking, and error handling.
 * Uses the /api/media/upload endpoint for all file types.
 *
 * @param {Object} options - Configuration options
 * @param {string} [options.category] - Restrict to specific media category: 'image' | 'video' | 'audio' | 'document' | 'all'
 * @param {Function} [options.onSuccess] - Callback when upload succeeds, receives { handle, file, category }
 * @param {Function} [options.onError] - Callback when upload fails, receives error message
 * @param {boolean} [options.showSuccessSnackbar=true] - Show success notification
 * @param {boolean} [options.showErrorSnackbar=true] - Show error notification
 * @param {string} [options.successMessage='File uploaded successfully'] - Custom success message
 * @returns {Object} Upload state and methods
 */
const useMediaUpload = (options = {}) => {
  const {
    category = 'all',
    onSuccess,
    onError,
    showSuccessSnackbar = true,
    showErrorSnackbar = true,
    successMessage = 'File uploaded successfully',
  } = options;

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileCategory, setFileCategory] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [handle, setHandle] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploaded, setUploaded] = useState(false);

  const { showSnackbar } = useUIStore();

  const reset = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(null);
    setFileName('');
    setFileCategory(null);
    setPreviewUrl('');
    setHandle('');
    setProgress(0);
    setUploading(false);
    setError('');
    setUploaded(false);
  }, [previewUrl]);

  const selectFile = useCallback(
    (selectedFile) => {
      if (!selectedFile) {
        setError('No file selected');
        return false;
      }

      // Validate the file
      const validation = validateMediaFile(selectedFile, category);
      if (!validation.valid) {
        setError(validation.error);
        if (showErrorSnackbar) {
          showSnackbar({ message: validation.error, severity: 'error' });
        }
        onError?.(validation.error);
        return false;
      }

      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      // Set file state
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setFileCategory(validation.category);
      setError('');
      setUploaded(false);
      setHandle('');

      // Create preview URL for images and videos
      if (validation.category === 'image' || validation.category === 'video') {
        const objectUrl = URL.createObjectURL(selectedFile);
        setPreviewUrl(objectUrl);
      } else {
        setPreviewUrl('');
      }

      return true;
    },
    [category, previewUrl, showErrorSnackbar, showSnackbar, onError]
  );

  const upload = useCallback(async () => {
    if (!file) {
      const errorMsg = 'No file selected for upload';
      setError(errorMsg);
      if (showErrorSnackbar) {
        showSnackbar({ message: errorMsg, severity: 'error' });
      }
      onError?.(errorMsg);
      return null;
    }

    try {
      setUploading(true);
      setProgress(0);
      setError('');

      const response = await uploadApi.uploadMedia(file, (percent) => {
        setProgress(percent || 0);
      });

      const data = response?.data || {};
      const mediaHandle = data.handle || data.media_id || data.id || '';

      setHandle(mediaHandle);
      setUploaded(true);
      setProgress(100);

      if (showSuccessSnackbar) {
        showSnackbar({ message: successMessage, severity: 'success' });
      }

      const result = {
        handle: mediaHandle,
        file,
        fileName,
        category: fileCategory,
        response: data,
      };

      onSuccess?.(result);
      return result;
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Upload failed';
      setError(errorMsg);

      if (showErrorSnackbar) {
        showSnackbar({ message: errorMsg, severity: 'error' });
      }

      onError?.(errorMsg);
      return null;
    } finally {
      setUploading(false);
    }
  }, [
    file,
    fileName,
    fileCategory,
    showSuccessSnackbar,
    showErrorSnackbar,
    successMessage,
    showSnackbar,
    onSuccess,
    onError,
  ]);

  const selectAndUpload = useCallback(
    async (selectedFile) => {
      const valid = selectFile(selectedFile);
      if (!valid) return null;

      // Need to upload the file directly since state won't update in same call
      try {
        setUploading(true);
        setProgress(0);
        setError('');

        const response = await uploadApi.uploadMedia(selectedFile, (percent) => {
          setProgress(percent || 0);
        });

        const data = response?.data || {};
        const mediaHandle = data.handle || data.media_id || data.id || '';
        const detectedCategory = getMediaCategory(selectedFile.type);

        setHandle(mediaHandle);
        setUploaded(true);
        setProgress(100);

        if (showSuccessSnackbar) {
          showSnackbar({ message: successMessage, severity: 'success' });
        }

        const result = {
          handle: mediaHandle,
          file: selectedFile,
          fileName: selectedFile.name,
          category: detectedCategory,
          response: data,
        };

        onSuccess?.(result);
        return result;
      } catch (err) {
        const errorMsg = err?.response?.data?.error || err?.message || 'Upload failed';
        setError(errorMsg);

        if (showErrorSnackbar) {
          showSnackbar({ message: errorMsg, severity: 'error' });
        }

        onError?.(errorMsg);
        return null;
      } finally {
        setUploading(false);
      }
    },
    [selectFile, showSuccessSnackbar, showErrorSnackbar, successMessage, showSnackbar, onSuccess, onError]
  );

  const handleFileInputChange = useCallback(
    (event, autoUpload = false) => {
      const selectedFile = event.target.files?.[0];
      if (!selectedFile) return;

      if (autoUpload) {
        selectAndUpload(selectedFile);
      } else {
        selectFile(selectedFile);
      }

      // Reset input value to allow re-selecting same file
      event.target.value = '';
    },
    [selectFile, selectAndUpload]
  );

  return {
    // State
    file,
    fileName,
    fileCategory,
    previewUrl,
    handle,
    progress,
    uploading,
    error,
    uploaded,

    // Methods
    selectFile,
    upload,
    selectAndUpload,
    handleFileInputChange,
    reset,

    // Helpers
    acceptString: FILE_ACCEPT_STRINGS[category] || FILE_ACCEPT_STRINGS.all,
    hasFile: !!file,
    canUpload: !!file && !uploading && !uploaded,
  };
};

export default useMediaUpload;
